import { useState } from "react";
import { Link } from "react-router-dom";
import { Check } from "lucide-react";
import { Slider } from "../ui/slider";
import { PLANS, calcPrice, money } from "../../lib/api";

export function PlanToggle({ plan, setPlan }) {
  return (
    <div className="inline-flex p-1 bg-slate-100 rounded-lg" role="tablist" data-testid="pricing-plan-toggle">
      {Object.entries(PLANS).map(([k, p]) => (
        <button key={k} onClick={() => setPlan(k)} role="tab" aria-selected={plan === k} data-testid={`pricing-plan-${k}`}
          className={`px-4 py-2 text-sm font-semibold rounded-md transition-colors ${plan === k ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-800"}`}>
          {p.label}{k !== "monthly" && <span className="ml-1.5 text-[11px] text-emerald-600">{k === "annual" ? "-17%" : "-10%"}</span>}
        </button>
      ))}
    </div>
  );
}

const Row = ({ label, value, testid, strong }) => (
  <div className={`flex justify-between py-2.5 ${strong ? "border-t border-white/15 pt-4 mt-1" : ""}`}>
    <span className={strong ? "font-semibold text-white" : "text-slate-300"}>{label}</span>
    <span className={`font-mono ${strong ? "text-xl font-semibold text-white" : "text-white"}`} data-testid={testid}>{value}</span>
  </div>
);

export default function PricingCalculator({ onCheckout, busy, initialPlan = "annual", initialUsers = 1, minUsers = 1 }) {
  const [plan, setPlan] = useState(initialPlan);
  const [users, setUsers] = useState(Math.max(initialUsers, minUsers));
  const p = PLANS[plan];
  const c = calcPrice(plan, users);
  const setU = (v) => setUsers(Math.min(100, Math.max(minUsers, Number(v) || minUsers)));
  return (
    <div className="grid lg:grid-cols-5 border border-slate-200 rounded-xl overflow-hidden bg-white" data-testid="pricing-calculator">
      <div className="lg:col-span-3 p-6 sm:p-8 space-y-8">
        <div>
          <p className="text-sm font-semibold text-slate-900 mb-3">1. Subscription duration</p>
          <PlanToggle plan={plan} setPlan={setPlan} />
          <p className="mt-3 text-sm text-slate-500">{p.billed}. Includes one user.</p>
        </div>
        <div>
          <div className="flex items-center justify-between mb-4">
            <p className="text-sm font-semibold text-slate-900">2. Number of users</p>
            <input type="number" min={minUsers} max={100} value={users} onChange={(e) => setU(e.target.value)} className="field w-24 text-center font-mono" data-testid="pricing-users-input" aria-label="Number of users" />
          </div>
          <Slider value={[users]} min={minUsers} max={50} step={1} onValueChange={(v) => setU(v[0])} data-testid="pricing-calculator-slider" />
          <p className="mt-3 text-sm text-slate-500">Additional users: {money(p.seat)} each {p.period}. Seats added mid-term are prorated.</p>
        </div>
        <ul className="grid sm:grid-cols-2 gap-2 text-sm text-slate-600">
          {["All IMS & QMS modules", "Business tools included", "Individual logins & roles", "Cancel renewal anytime"].map((t) => (
            <li key={t} className="flex gap-2"><Check className="w-4 h-4 text-emerald-600 mt-0.5" />{t}</li>
          ))}
        </ul>
      </div>
      <div className="lg:col-span-2 bg-[#0A192F] p-6 sm:p-8 flex flex-col">
        <p className="eyebrow !text-sky-400">Your estimate</p>
        <div className="mt-4 text-sm flex-1">
          <Row label={`Base subscription (${p.label})`} value={money(c.base)} testid="calc-base" />
          <Row label={`Additional users (${c.extra} × ${money(p.seat)})`} value={money(c.extraCost)} testid="calc-extra" />
          <Row label="Total payable" value={money(c.total)} testid="calc-total" strong />
          <Row label="Billing frequency" value={p.period} testid="calc-frequency" />
          <Row label="Equivalent monthly cost" value={money(c.monthly)} testid="calc-monthly" />
          <Row label="Savings vs monthly billing" value={money(c.savings)} testid="calc-savings" />
          <p className="text-xs text-slate-400 mt-4 leading-relaxed">All prices in Australian dollars. GST treatment will be confirmed before live payments are activated.</p>
        </div>
        {onCheckout ? (
          <button disabled={busy} onClick={() => onCheckout(plan, users)} className="mt-6 w-full bg-sky-500 hover:bg-sky-400 disabled:opacity-60 text-white font-semibold py-3 rounded-md transition-colors" data-testid="calc-checkout-btn">
            {busy ? "Redirecting to secure checkout…" : `Subscribe — ${money(c.total)}`}
          </button>
        ) : (
          <Link to={`/register?plan=${plan}&users=${users}`} className="mt-6 block text-center w-full bg-sky-500 hover:bg-sky-400 text-white font-semibold py-3 rounded-md transition-colors" data-testid="calc-start-trial-btn">Start Your 5-Day Free Trial</Link>
        )}
      </div>
    </div>
  );
}
